import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, take, takeUntil } from 'rxjs';
import { TrainingCategoryModel } from '../../../models/training-category.model';
import { AuthService } from '../../../services/auth.service';
import { CartService } from '../../../services/cart.service';
import { TrainingCategoryService } from '../../../services/training-category.service';
import { UserService } from '../../../services/user.service';
import { TopicRequestDialogComponent } from '../../dialogs/topic-request/topic-request-dialog.component';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy {
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const mobileMenu = document.querySelector('.mobile-menu-content');
    const mobileMenuTrigger = document.querySelector('.mobile-menu-trigger');
    if (mobileMenuTrigger && mobileMenuTrigger.contains(target)) {
      return;
    }
    if (mobileMenu && !mobileMenu.contains(target) && this.showMobileMenu) {
      this.showMobileMenu = false;
    }
  }

  public isLoading = true;
  private destroy$ = new Subject<void>();
  public courseTypes: TrainingCategoryModel[] = [];
  public showMobileMenu = false;
  public sections = {
    user: false,
    trainingCategories: false,
    service: false,
    about: false,
  };

  constructor(
    public userService: UserService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: DialogService,
    private courseTypeService: TrainingCategoryService,
    public cartService: CartService
  ) {
    this.getCourseTypes();
  }

  public userMenuItems = [];

  public trainingCategoriesMenuItems = [];

  public serviceMenuItems = [
    {
      label: 'Für Teilnehmende',
      link: 'service/participants',
      command: () => {
        this.hideMobileMenu();
      },
    },
    {
      label: 'Für Dozierende',
      link: 'service/lecturers',
      command: () => {
        this.hideMobileMenu();
      },
    },
    {
      label: 'Für (Hoch-)Schulen',
      link: 'service/colleges',
      command: () => {
        this.hideMobileMenu();
      },
    },
  ];

  public aboutMenuItems = [
    {
      label: 'Leitbild',
      link: 'about/philosophy',
      command: () => {
        this.hideMobileMenu();
      },
    },
    {
      label: 'Team',
      link: 'about/team',
      command: () => {
        this.hideMobileMenu();
      },
    },
    {
      label: 'Dozierende',
      link: 'about/lecturers',
      command: () => {
        this.hideMobileMenu();
      },
    },
    {
      label: 'Kooperationen',
      link: 'about/cooperations',
      command: () => {
        this.hideMobileMenu();
      },
    },
    {
      label: 'Kontakt',
      link: 'about/contact',
      command: () => {
        this.hideMobileMenu();
      },
    },
  ];

  public ngOnInit(): void {
    this.userService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(currentUser => {
        this.updateMenuItems(currentUser);
      });
  }

  private hideMobileMenu() {
    this.showMobileMenu = false;
    this.sections = {
      user: false,
      trainingCategories: false,
      service: false,
      about: false,
    };
  }

  public toggleSection(section: string) {
    this.sections[section] = !this.sections[section];
  }

  public getCategoryNavLink(id: number) {
    return 'advancedtraining/' + btoa(String(id));
  }

  public route(route: string) {
    this.router.navigate([route], { relativeTo: this.activatedRoute });
  }

  public openCourseType(courseType: TrainingCategoryModel) {
    this.router.navigate(['advancedtraining', btoa(String(courseType.id))], {
      relativeTo: this.activatedRoute,
    });
  }

  private updateMenuItems(currentUser: any): void {
    this.userMenuItems = [
      {
        label: 'Meine Termine',
        icon: 'fa-regular fa-calendar-heart',
        link: 'user',
        queryParams: { tab: 'appointments' },
        command: () => {
          this.hideMobileMenu();
        },
      },
      {
        label: 'Seminar-Unterlagen',
        icon: 'fa-regular fa-book-copy',
        link: 'user/library',
        command: () => {
          this.hideMobileMenu();
        },
      },
      {
        // new user area library
        label: 'Bibliothek',
        icon: 'fa-regular fa-folders',
        link: '/member-areas',
      },
      {
        label: 'Mein Konto',
        icon: 'fa-regular fa-user',
        link: 'user',
        queryParams: { tab: 'account' },
        command: () => {
          this.hideMobileMenu();
        },
      },
      ...(currentUser?.roleId === 1
        ? [
            {
              label: 'Administration',
              icon: 'fa-regular fa-user-crown',
              link: 'administration',
              command: () => {
                this.hideMobileMenu();
              },
            },
          ]
        : []),
      {
        label: 'Abmelden',
        icon: 'fa-regular fa-arrow-right-from-bracket',
        command: () => {
          this.hideMobileMenu();
          this.logOut();
        },
      },
    ];
  }

  private getCourseTypes() {
    this.courseTypeService
      .getAllTrainingCategories()
      .pipe(take(1))
      .subscribe(courseTypes => {
        this.courseTypes = courseTypes;
        this.trainingCategoriesMenuItems = [
          ...this.courseTypes.map(courseType => {
            return {
              label: courseType.name,
              link: this.getCategoryNavLink(courseType.id),
              command: () => {
                this.hideMobileMenu();
              },
            };
          }),
          {
            label: 'Themenwunsch?',
            link: null,
            command: () => {
              this.hideMobileMenu();
              this.openDialog();
            },
          },
        ];
        this.isLoading = false;
      });
  }

  public logOut() {
    this.authService.logOut();
    this.router.navigate(['./login']);
  }

  public onLogin() {
    const noRedirectUrls = ['/', '/login', '/register'];
    const redirectUrl = !noRedirectUrls.includes(this.router.url)
      ? this.router.url
      : null;

    this.router.navigate(['./login'], {
      queryParams: { redirectUrl },
    });
  }

  openDialog() {
    this.dialog.open(TopicRequestDialogComponent, {
      width: '655px',
      breakpoints: {
        '768px': '90vw',
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
